import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { Status as ClientStatus } from 'store/clients/clientsSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NoteForm } from '../NoteForm';
import { DraftNoteFormProvider, useNoteFormContext } from '../context';
import { DraftActions } from './NoteFormSections';

type DraftNoteFormProps = {
  status: ClientStatus | ClientStatus['id'];
  clientId: string;
  clientName: string;
  prependedClientId: string;
  note?: Partial<ClientProfileContentItem>;
  expanded?: boolean;
  onSubmitSuccess?: () => void;
  onDeleteSuccess?: () => void;
  onOpenAiScribeDrawer?: () => void;
};

const DraftNoteFormContent = ({ expanded }: { expanded: boolean }) => {
  const { enableDateTimeUpdates, enableMpiUpdate } = useFlags();
  const { isManagerReview, disabled, isAiScribeEnabled } = useNoteFormContext();

  return (
    <NoteForm.Form>
      <NoteForm.FieldsContainer expanded={enableMpiUpdate ? true : expanded}>
        {enableMpiUpdate && isAiScribeEnabled && (
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.AiScribeButton />
          </NoteForm.FieldsContainer.HalfRow>
        )}
        {enableMpiUpdate && (
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.Note disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        )}
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.NoteType disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.DateOfEncounter disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>

        {enableDateTimeUpdates ? (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        ) : (
          !isManagerReview && (
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.StartTime disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.TimeSpent disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
          )
        )}

        {isManagerReview && (
          <>
            {enableDateTimeUpdates ? (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.FullWidth>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.FullWidth>
                </NoteForm.FieldsContainer.Row>
              </>
            ) : (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.TimeSpent disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
              </>
            )}
          </>
        )}

        {!isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.NextContact disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.FullWidth>
              <NoteForm.Fields.TypeOfSupport disabled={disabled} />
            </NoteForm.FieldsContainer.FullWidth>
          </>
        )}
      </NoteForm.FieldsContainer>
      {!enableMpiUpdate && (
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.Note disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
      )}
      <NoteForm.FieldsContainer.HalfRow>
        <NoteForm.Fields.DocumentationTime disabled={disabled} />
      </NoteForm.FieldsContainer.HalfRow>
      <DraftActions />
    </NoteForm.Form>
  );
};

/**
 * Preset component for editing draft notes with autosave.
 * This is the pattern used in the notes section (noteSection context).
 */
export const DraftNoteForm = ({
  status,
  clientId,
  note,
  expanded = true,
  clientName,
  prependedClientId,
  onSubmitSuccess,
  onDeleteSuccess,
  onOpenAiScribeDrawer
}: DraftNoteFormProps) => {
  return (
    <DraftNoteFormProvider
      status={status}
      clientId={clientId}
      clientName={clientName}
      prependedClientId={prependedClientId}
      note={note}
      disabled={false}
      onSubmitSuccess={onSubmitSuccess}
      onDeleteSuccess={onDeleteSuccess}
      onOpenAiScribeDrawer={onOpenAiScribeDrawer}
    >
      <NoteForm.Root>
        <DraftNoteFormContent expanded={expanded} />
      </NoteForm.Root>
    </DraftNoteFormProvider>
  );
};
